/**
 * MonolithSkillEffect — Earth tree wall path apotheosis.
 *
 * Channel for 1.5 seconds to raise an indestructible 5-tile stone structure
 * for 15 seconds. On completion: 16 damage to any enemies inside the
 * structure area, all launched clear. While the Monolith stands: Earth
 * skill cooldowns tick 30% faster and every active construction doubles
 * in HP.
 *
 * State machine: idle -> channeling -> active -> cooldown -> idle.
 *   idle:       ready to activate
 *   channeling: 1.5s hold; deactivate cancels with no effect
 *   active:     Monolith placed, buffs running, 15s timer
 *   cooldown:   Monolith expired, very_long cooldown
 *
 * Cooldown starts when the Monolith expires, not on channel completion.
 * This rewards keeping the Monolith alive (analogous to Fortify's
 * cooldown-on-break design).
 *
 * The HP-doubling uses the same delta-on-enter/exit pattern as
 * WeightBearingSkillEffect: additive bonus applied on activation,
 * clamped back to base config.hp on expiry.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Monolith — Apotheosis
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ActiveTerrain } from '../../../systems/TerrainConstructionSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface MonolithDeps {
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly terrain: {
    place(config: {
      type: string;
      position: { x: number; y: number };
      widthTiles: number;
      heightTiles: number;
      hp: number;
      durationMs: number;
      sourceSkillId: string;
      blocksMovement: boolean;
      blocksProjectiles: boolean;
      indestructible?: boolean;
    }): string;
    getAll(): readonly ActiveTerrain[];
  };
  readonly pathfinding: {
    markImpassable(tiles: { x: number; y: number }[]): void;
  };
  readonly targeting: {
    getEnemiesInRadius(radiusPx: number): string[];
    getEnemyPosition(enemyId: string): { x: number; y: number };
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
    applyKnockback(
      targetId: string,
      normalizedDir: { x: number; y: number },
      tiles: number,
    ): void;
  };
  readonly cooldownHooks: {
    register(hook: {
      id: string;
      trigger: { type: 'onCondition'; conditionId: string };
      targetSkillId: string;
      effect: { type: 'accelerate'; multiplier: number };
    }): void;
    unregister(hookId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface MonolithConfig {
  /** Channel hold duration in ms before effect fires. */
  readonly channelMs: number;
  /** Duration the Monolith remains active after channel completes. */
  readonly activeDurationMs: number;
  /** Very_long cooldown tier — starts on Monolith expiry. */
  readonly cooldownMs: number;
  /** Damage dealt to enemies inside the footprint on placement. */
  readonly damage: number;
  /** Knockback tiles applied to enemies on placement (launch clear). */
  readonly knockbackTiles: number;
  /** Width of the Monolith structure in tiles. */
  readonly widthTiles: number;
  /** Cooldown acceleration multiplier applied to Earth skills. */
  readonly accelerationMultiplier: number;
  /** HP bonus multiplier for active terrain (1.0 = double). */
  readonly hpBonusMultiplier: number;
  /** World units per tile. */
  readonly tileSizePx: number;
  /** Earth skill IDs to register cooldown acceleration hooks for. */
  readonly earthSkillIds: readonly string[];
}

const DEFAULT_CONFIG: MonolithConfig = {
  channelMs: 1500,
  activeDurationMs: 15000,
  cooldownMs: 4000,
  damage: 16,
  knockbackTiles: 2,
  widthTiles: 5,
  accelerationMultiplier: 1.3,
  hpBonusMultiplier: 1.0,
  tileSizePx: 32,
  earthSkillIds: [],
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type MonolithPhase = 'idle' | 'channeling' | 'active' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class MonolithSkillEffect implements SkillEffect {
  readonly skillId = 'monolith';
  readonly type = 'active' as const;
  readonly activationMode = 'channel' as const;

  private readonly deps: MonolithDeps;
  private readonly config: MonolithConfig;

  private _phase: MonolithPhase = 'idle';
  private _phaseTimer = 0;

  // Monolith terrain tracking
  private _monolithTerrainId: string | null = null;
  private _footprintTiles: { x: number; y: number }[] = [];

  // HP doubling tracking (Weight-Bearing delta pattern)
  private _hpBonusApplied = new Set<string>();

  // Cooldown hook IDs for cleanup on expiry
  private _registeredHookIds: string[] = [];

  // Telemetry for scenario validation
  private _channelComplete = false;
  private _damageTargetCount = 0;
  private _pathfindingCallCount = 0;

  constructor(deps: MonolithDeps, config: Partial<MonolithConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): MonolithPhase { return this._phase; }
  get channelComplete(): boolean { return this._channelComplete; }
  get monolithTerrainId(): string | null { return this._monolithTerrainId; }
  get footprintTiles(): readonly { x: number; y: number }[] { return this._footprintTiles; }
  get damageTargetCount(): number { return this._damageTargetCount; }
  get pathfindingCallCount(): number { return this._pathfindingCallCount; }
  get registeredHookIds(): readonly string[] { return this._registeredHookIds; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (this._phase !== 'idle') return false;

    this._phase = 'channeling';
    this._phaseTimer = this.config.channelMs;
    this._channelComplete = false;
    this._monolithTerrainId = null;
    this._footprintTiles = [];
    this._damageTargetCount = 0;

    return true;
  }

  /** Deactivate: if channeling, cancel the channel with no effect. */
  deactivate(): void {
    if (this._phase === 'channeling') {
      this._phase = 'idle';
      this._phaseTimer = 0;
    }
  }

  isActive(): boolean {
    return this._phase === 'channeling' || this._phase === 'active';
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    this._phaseTimer -= delta;

    if (this._phase === 'channeling' && this._phaseTimer <= 0) {
      this._onChannelComplete();
      this._phase = 'active';
      this._phaseTimer = this.config.activeDurationMs;
      return;
    }

    if (this._phase === 'active') {
      // Apply HP doubling to any terrain placed while Monolith is active
      this._applyHpBonusToUncovered();

      if (this._phaseTimer <= 0) {
        this._onMonolithExpiry();
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
      }
      return;
    }

    if (this._phase === 'cooldown' && this._phaseTimer <= 0) {
      this._phase = 'idle';
      this._phaseTimer = 0;
    }
  }

  destroy(): void {
    if (this._phase === 'active') {
      this._removeHpBonusFromAll();
      this._unregisterCooldownHooks();
    }
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- Internal ---

  private _onChannelComplete(): void {
    this._channelComplete = true;

    const { x, y } = this.deps.player.getPosition();
    const t = this.config.tileSizePx;

    // Place the Monolith as a single terrain entity at the player's position
    this._monolithTerrainId = this.deps.terrain.place({
      type: 'wall',
      position: { x, y },
      widthTiles: this.config.widthTiles,
      heightTiles: 1,
      hp: Infinity,
      durationMs: this.config.activeDurationMs,
      sourceSkillId: 'monolith',
      blocksMovement: true,
      blocksProjectiles: true,
      indestructible: true,
    });

    // Compute the 5-tile horizontal footprint centered on the player
    const halfW = Math.floor(this.config.widthTiles / 2);
    const tiles: { x: number; y: number }[] = [];
    for (let i = -halfW; i <= halfW; i++) {
      tiles.push({ x: x + i * t, y });
    }
    this._footprintTiles = tiles;

    // Damage and launch enemies within the footprint
    this._launchEnemiesInFootprint(x, y, t);

    // Mark footprint as impassable for pathfinding
    this.deps.pathfinding.markImpassable(tiles);
    this._pathfindingCallCount++;

    // Register cooldown acceleration hooks for all Earth skills
    this._registerCooldownHooks();

    // Double HP on all active terrain (Weight-Bearing delta pattern)
    this._applyHpBonusToUncovered();
  }

  private _launchEnemiesInFootprint(
    centerX: number,
    centerY: number,
    tileSizePx: number,
  ): void {
    const halfW = Math.floor(this.config.widthTiles / 2);
    const halfFootprintPx = (halfW + 0.5) * tileSizePx;
    const halfTilePx = tileSizePx * 0.5;

    // Search radius covering the full footprint with margin
    const searchRadius = halfFootprintPx + tileSizePx;
    const candidates = this.deps.targeting.getEnemiesInRadius(searchRadius);
    this._damageTargetCount = 0;

    for (const enemyId of candidates) {
      const pos = this.deps.targeting.getEnemyPosition(enemyId);

      // Check if enemy position falls within the footprint bounding box
      if (
        Math.abs(pos.x - centerX) <= halfFootprintPx &&
        Math.abs(pos.y - centerY) <= halfTilePx
      ) {
        this.deps.combat.applyDamage(enemyId, this.config.damage, {
          sourceSkillId: 'monolith',
        });

        // Knockback direction: away from Monolith center
        const dx = pos.x - centerX;
        const dy = pos.y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const nx = dist > 0 ? dx / dist : 0;
        const ny = dist > 0 ? dy / dist : 1;

        this.deps.combat.applyKnockback(
          enemyId,
          { x: nx, y: ny },
          this.config.knockbackTiles,
        );

        this._damageTargetCount++;
      }
    }
  }

  private _registerCooldownHooks(): void {
    this._registeredHookIds = [];
    for (const skillId of this.config.earthSkillIds) {
      const hookId = `monolith_accel_${skillId}`;
      this.deps.cooldownHooks.register({
        id: hookId,
        trigger: { type: 'onCondition', conditionId: 'monolith_active' },
        targetSkillId: skillId,
        effect: {
          type: 'accelerate',
          multiplier: this.config.accelerationMultiplier,
        },
      });
      this._registeredHookIds.push(hookId);
    }
  }

  private _unregisterCooldownHooks(): void {
    for (const hookId of this._registeredHookIds) {
      this.deps.cooldownHooks.unregister(hookId);
    }
    this._registeredHookIds = [];
  }

  /**
   * Applies the HP-doubling bonus to any active terrain not yet in
   * `_hpBonusApplied`. Skips the Monolith itself (indestructible, Infinity HP).
   * Same delta pattern as WeightBearingSkillEffect.
   */
  private _applyHpBonusToUncovered(): void {
    for (const terrain of this.deps.terrain.getAll()) {
      if (terrain.terrainId === this._monolithTerrainId) continue;
      if (this._hpBonusApplied.has(terrain.terrainId)) continue;

      const bonus = terrain.config.hp * this.config.hpBonusMultiplier;
      const maxWithBonus = terrain.config.hp * (1 + this.config.hpBonusMultiplier);
      terrain.hp = Math.min(terrain.hp + bonus, maxWithBonus);
      this._hpBonusApplied.add(terrain.terrainId);
    }
  }

  /**
   * Removes the HP bonus from all active terrain by clamping each object's
   * HP back to its base config.hp. Damage taken while the bonus was active
   * is preserved (only excess HP above base max is removed).
   */
  private _removeHpBonusFromAll(): void {
    for (const terrain of this.deps.terrain.getAll()) {
      if (terrain.terrainId === this._monolithTerrainId) continue;
      terrain.hp = Math.min(terrain.hp, terrain.config.hp);
    }
    this._hpBonusApplied.clear();
  }

  private _onMonolithExpiry(): void {
    this._removeHpBonusFromAll();
    this._unregisterCooldownHooks();
    // Pathfinding tile restoration is handled by the integration layer
    // when TerrainConstructionSystem emits the 'expired' event.
  }
}
